// servicesContent.js

const UC_IMG = (file) => `/images/Service/${file}`;

// Normalise icon keys for FontAwesome map
const FIX_ICON = (icon) => {
  if (!icon) return "check";
  const s = String(icon).trim();

  const map = {
    usershield: "userShield",
    user_shield: "userShield",
    "user-shield": "userShield",

    // aliases
    outcome: "check",
    ok: "check",

    // keep existing keys as-is if they match your FA_ICONS
    shield: "shield",
    check: "check",
    layers: "layers",
    plug: "plug",
    route: "route",
    doc: "doc",
    gear: "gear",
    training: "training",
    warehouse: "warehouse",
    industry: "industry",
    store: "store",
    truck: "truck",
    building: "building",
    mobile: "mobile",
    apps: "apps",
    lock: "lock",
    support: "support",
    roadmap: "roadmap",
    m365: "m365",
    headset: "headset",
    office: "office",
    scan: "scan",
    dashboard: "dashboard",
    quality: "quality",
    globe: "globe",
    report: "report",
    data: "data",
    runbook: "runbook",
    bank: "bank",

    // NEW for your dev service
    radar: "radar",
    design: "design",
    api: "api",
    deploy: "deploy",
    code: "code",
  };

  return map[s] || s; // fallback: keep provided key
};

const toStats = (service) => {
  // Your UI reads content.stats
  if (Array.isArray(service.stats) && service.stats.length) return service;
  if (Array.isArray(service.kpis) && service.kpis.length) {
    return { ...service, stats: service.kpis };
  }
  return service;
};

const withUseCaseImages = (service, images = []) => {
  if (!service) return service;

  const base = toStats(service);

  return {
    ...base,
    badge: base.badge || "Service",
    heroAccent: base.heroAccent || base.subtitle || "Solution premium",
    tags: Array.isArray(base.tags) ? base.tags : [],
    scopeCards: (base.scopeCards || []).map((c) => ({
      ...c,
      icon: FIX_ICON(c.icon),
    })),
    deliverables: (base.deliverables || []).map((d) => ({
      ...d,
      icon: FIX_ICON(d.icon),
    })),
    packs: (base.packs || []).map((p) => ({
      ...p,
      items: Array.isArray(p.items) ? p.items : [],
    })),
    useCases: (base.useCases || []).map((u, idx) => ({
      ...u,
      icon: FIX_ICON(u.icon),
      // support both "outcome" and "result"
      result: u.result ?? u.outcome ?? "",
      image:
        u.image ||
        (images[idx] ? UC_IMG(images[idx]) : UC_IMG("placeholder-usecase.webp")),
    })),
  };
};

export const SERVICES_CONTENT = {
  cybersecurite: withUseCaseImages(
    {
      key: "cybersecurite",
      title: "Cybersécurité",
      subtitle: "Fortinet • Protection Enterprise",
      heroImage: "/images/Service/4.webp",
      heroTitle: "Cybersécurité Enterprise",
      heroText:
        "Protégez vos réseaux, sites distants et accès utilisateurs avec une approche structurée : audit, architecture, déploiement, supervision et amélioration continue. Objectif : réduire le risque, sécuriser l’accès et garantir la continuité.",
      tags: ["Fortinet", "VPN", "Firewall", "Segmentation", "Monitoring", "Bonnes pratiques"],
      kpis: [
        { value: "≤ 10j", label: "Déploiement (selon périmètre)" },
        { value: "99,9%", label: "Objectif de disponibilité" },
        { value: "24/7", label: "Option support" },
      ],
      scopeCards: [
        { icon: "shield", title: "Sécurisation réseau", text: "Firewall, règles, segmentation, durcissement, politiques de sécurité adaptées à votre contexte (multi-sites, WAN, accès distants)." },
        { icon: "usershield", title: "Accès utilisateurs", text: "VPN, MFA (si applicable), profils d’accès, contrôle des droits et sécurisation des connexions externes." },
        { icon: "radar", title: "Détection & supervision", text: "Surveillance, alerting, visibilité sur incidents et recommandations d’amélioration (logs, events, santé)." },
        { icon: "check", title: "Conformité & réduction des risques", text: "Approche orientée conformité et continuité de service : documentation, procédures, audits réguliers (selon option)." },
      ],
      deliverables: [
        { icon: "doc", title: "Dossier d’architecture", text: "Schémas, règles, flux, choix techniques et justification." },
        { icon: "gear", title: "Configuration & mise en service", text: "Paramétrage, tests, validation avec vos équipes." },
        { icon: "training", title: "Transfert de compétences", text: "Formation + bonnes pratiques d’exploitation." },
      ],
      packs: [
        { name: "Essentiel", tag: "Mise en place", accent: "basic", highlight: "Déploiement standard + documentation.", items: ["Audit & cadrage", "Déploiement standard", "Documentation de base", "Support 8x5 (option)"] },
        { name: "Opérationnel", tag: "Recommandé", accent: "gold", highlight: "Pilotage + monitoring et revues périodiques.", items: ["Tout Essentiel", "Monitoring & alerting", "Revue de configuration (périodique)", "Support étendu (option)"] },
        { name: "Critique", tag: "SLA", accent: "dark", highlight: "Engagements renforcés pour environnements critiques.", items: ["Tout Opérationnel", "Astreinte (option)", "Priorisation incidents", "Engagements renforcés"] },
      ],
      useCases: [
        { icon: "building", title: "Multi-sites & agences", text: "Sécuriser les accès inter-sites, standardiser les règles et centraliser la visibilité.", outcome: "Moins d’incidents & meilleure continuité." },
        { icon: "truck", title: "Logistique & transport", text: "Sécuriser les échanges entre terminaux terrain et SI (ERP/WMS), accès distants contrôlés.", outcome: "Traçabilité + réduction du risque." },
        { icon: "industry", title: "Industrie & OT/IT", text: "Segmentation et protection des environnements sensibles, contrôle des flux et durcissement.", outcome: "Sécurité sans impacter la production." },
      ],
      proof:
        "Approche structurée : audit → déploiement → supervision → amélioration continue. Chaque étape est validée avec vos équipes pour garantir la réussite.",
      ctas: [
        { label: "Demander un audit", to: "/contactus" },
        { label: "Parler à un expert", to: "/contactus" },
      ],
    },
    ["cyber-multisite.webp", "cyber-logistics.webp", "cyber-industry.webp"]
  ),

  "impression-managee": withUseCaseImages(
    {
      key: "impression-managee",
      catalogue: {
        title: "Équipements et consommables pour votre projet d’impression",
        text: "Comparez les références du catalogue avec votre format d’étiquette, vos volumes et votre logiciel. La compatibilité des consommables et les prestations de configuration sont à confirmer dans votre devis.",
        links: [
          { label: "Comparer les imprimantes d’étiquettes", href: "https://store.teledyne.ma/produits/imprimantes/imprimantes-codes-barres/" },
          { label: "Choisir les étiquettes et rubans", href: "https://store.teledyne.ma/produits/consommables/" },
        ],
      },
      title: "Impression managée",
      heroImage: "/images/Service/3.webp",
      subtitle: "CodeSoft • BarTender • Standardisation",
      heroTitle: "Impression d’étiquettes maîtrisée",
      heroText:
        "Standardisez vos modèles, automatisez l’impression depuis vos systèmes (ERP/WMS) et fiabilisez la traçabilité. Objectif : moins d’erreurs, plus de vitesse et une conformité renforcée.",
      tags: ["CodeSoft", "BarTender", "Étiquetage", "Templates", "Automatisation", "Traçabilité"],
      kpis: [
        { value: "100%", label: "Modèles standardisés" },
        { value: "-40%", label: "Temps gagné (objectif)" },
        { value: "Stable", label: "Qualité d’impression" },
      ],
      scopeCards: [
        { icon: "layers", title: "Templates centralisés", text: "Modèles d’étiquettes harmonisés et maintenables (versions, droits, validation)." },
        { icon: "plug", title: "Intégration SI", text: "Connexion et automatisation via ERP/WMS (selon faisabilité technique)." },
        { icon: "route", title: "Routage & multi-sites", text: "Routage des impressions, règles par site/atelier, haute disponibilité (option)." },
        { icon: "check", title: "Conformité & traçabilité", text: "Réduction des erreurs d’étiquetage, cohérence des champs, auditabilité améliorée." },
      ],
      deliverables: [
        { icon: "doc", title: "Bibliothèque de modèles", text: "Modèles validés + convention de nommage." },
        { icon: "gear", title: "Paramétrage & tests", text: "Tests terrain + scénario de validation." },
        { icon: "training", title: "Formation", text: "Formation utilisateurs + admin + bonnes pratiques." },
      ],
      packs: [
        { name: "Essentiel", tag: "Mise en place", accent: "basic", highlight: "Standardisation + paramétrage.", items: ["Templates & standardisation", "Paramétrage", "Documentation", "Support 8x5 (option)"] },
        { name: "Opérationnel", tag: "Recommandé", accent: "gold", highlight: "Automatisation + routage.", items: ["Tout Essentiel", "Automatisation ERP/WMS", "Routage impressions", "Revue qualité (périodique)"] },
        { name: "Critique", tag: "SLA", accent: "dark", highlight: "Continuité renforcée (option).", items: ["Tout Opérationnel", "Redondance / continuité (option)", "Priorisation incidents", "Engagements renforcés"] },
      ],
      useCases: [
        { icon: "warehouse", title: "Entrepôt & préparation", text: "Étiquettes logistiques homogènes (SSCC, colis, palettes) et impression rapide au poste.", outcome: "Moins d’erreurs, meilleure productivité." },
        { icon: "industry", title: "Production & traçabilité", text: "Étiquettes de lots/séries conformes, traçabilité sur lignes et postes d’emballage.", outcome: "Conformité + audit plus simple." },
        { icon: "store", title: "Distribution & retail", text: "Étiquettes prix/produits, flux multi-sites, règles d’impression par magasin.", outcome: "Standardisation et contrôle." },
      ],
      proof:
        "Nous sécurisons la chaîne d’impression : modèle → données → imprimante. Résultat : traçabilité fiable et réduction des erreurs opérationnelles.",
      ctas: [
        { label: "Optimiser l’impression", to: "/contactus" },
        { label: "Demander une démo", to: "/contactus" },
      ],
    },
    ["print-warehouse.webp", "print-industry.webp", "print-retail.webp"]
  ),

  "gestion-mdm": withUseCaseImages(
    {
      key: "gestion-mdm",
      catalogue: {
        title: "Choisir les terminaux de votre parc mobile",
        text: "Consultez les PDA et tablettes du catalogue, puis précisez vos applications, votre connectivité et vos horaires de travail. La compatibilité MDM, la configuration et les accessoires doivent être validés pour chaque référence.",
        links: [
          { label: "Comparer les terminaux PDA", href: "https://store.teledyne.ma/produits/terminaux-mobiles/terminaux-pda/" },
          { label: "Comparer les tablettes industrielles", href: "https://store.teledyne.ma/produits/terminaux-mobiles/tablettes-industrielles/" },
        ],
      },
      title: "Gestion MDM",
      subtitle: "Gestion centralisée des terminaux",
      heroImage: "/images/Service/6.webp",
      heroTitle: "Gérez et sécurisez vos flottes",
      heroText:
        "Déployez, sécurisez et supervisez vos terminaux mobiles, PDA et tablettes. Objectif : moins d’incidents, déploiements plus rapides, contrôle des politiques et visibilité terrain.",
      tags: ["SOTI", "Support à distance", "Politiques de sécurité"],
      kpis: [
        { value: "À distance", label: "Déploiement apps" },
        { value: "Policy", label: "Sécurité & conformité" },
        { value: "Support", label: "Option 24/7" },
      ],
      scopeCards: [
        { icon: "mobile", title: "Enrôlement & profils", text: "Onboarding, profils par métier/site, restrictions, configurations standards." },
        { icon: "apps", title: "Déploiement applicatif", text: "Déploiement, mises à jour, contrôle versions, rollback (selon plateforme)." },
        { icon: "lock", title: "Sécurité & conformité", text: "Chiffrement, politique mot de passe, contrôle des apps et paramètres." },
        { icon: "support", title: "Support & supervision", text: "Suivi incidents, visibilité parc, recommandations et amélioration continue." },
      ],
      deliverables: [
        { icon: "doc", title: "Stratégie MDM", text: "Profils, règles, gouvernance et bonnes pratiques." },
        { icon: "gear", title: "Plateforme opérationnelle", text: "Tenant/serveur prêt, profils, policies et tests." },
        { icon: "training", title: "Transfert & runbook", text: "Procédures d’exploitation + formation admin." },
      ],
      packs: [
        { name: "Essentiel", tag: "Mise en place", accent: "basic", highlight: "Onboarding + profils + déploiement de base.", items: ["Enrôlement & profils", "Déploiement de base", "Documentation", "Support 8x5 (option)"] },
        { name: "Opérationnel", tag: "Recommandé", accent: "gold", highlight: "Alerting et suivi parc.", items: ["Tout Essentiel", "Policies avancées", "Suivi parc & alerting", "Revue mensuelle (option)"] },
        { name: "Critique", tag: "SLA", accent: "dark", highlight: "Engagements renforcés (option).", items: ["Tout Opérationnel", "Astreinte (option)", "Priorisation incidents", "Engagements renforcés"] },
      ],
      useCases: [
        { icon: "warehouse", title: "PDA logistique", text: "Déploiement WMS, configurations par site, limitation des usages hors métier.", outcome: "Parc stable et productif." },
        { icon: "truck", title: "Flotte terrain", text: "Applications métier, politiques de sécurité, géolocalisation selon besoins (si autorisé).", outcome: "Moins de pannes & meilleure maîtrise." },
      ],
      proof:
        "Nous industrialisons la gestion de parc : standardisation + sécurité + déploiement continu. Vous gagnez en contrôle et en disponibilité sur le terrain.",
      ctas: [
        { label: "Structurer votre parc", to: "/contactus" },
        { label: "Parler MDM", to: "/contactus" },
      ],
    },
    ["mdm-warehouse.webp", "mdm-field.webp"]
  ),

  "conseil-support-it": withUseCaseImages(
    {
      key: "conseil-support-it",
      title: "Conseil & support IT",
      heroImage: "/images/Service/5.webp",
      subtitle: "Microsoft 365 • Sécurité postes",
      heroTitle: "Support IT orienté continuité",
      heroText:
        "Accompagnement Microsoft 365, sécurité des postes et support technique fiable. Objectif : réduire les interruptions, améliorer la productivité et sécuriser les usages au quotidien.",
      tags: ["Microsoft 365", "Sécurité postes", "Support", "Continuité", "Bonnes pratiques"],
      kpis: [
        { value: "< 4h", label: "Objectif de réponse (option)" },
        { value: "Local", label: "Proximité & terrain" },
        { value: "SLA", label: "Optionnel" },
      ],
      scopeCards: [
        { icon: "m365", title: "Microsoft 365", text: "Configuration, assistance, bonnes pratiques et optimisation des usages (selon périmètre)." },
        { icon: "shield", title: "Sécurité endpoint", text: "Recommandations, déploiement et suivi de solutions de sécurité postes (selon stack)." },
        { icon: "headset", title: "Support & incidents", text: "Diagnostic, résolution, escalade et suivi avec visibilité sur priorités." },
        { icon: "roadmap", title: "Conseil & évolution", text: "Feuille de route IT, optimisation et amélioration continue." },
      ],
      deliverables: [
        { icon: "doc", title: "Plan d’action IT", text: "Recommandations priorisées et quick wins." },
        { icon: "gear", title: "Mise en œuvre", text: "Configuration, déploiement, validation avec vos équipes." },
        { icon: "training", title: "Guides & transfert", text: "Procédures, bonnes pratiques et formation." },
      ],
      packs: [
        { name: "Essentiel", tag: "Support", accent: "basic", highlight: "Support 8x5 + procédures.", items: ["Support 8x5", "Gestion incidents", "Documentation", "Conseils ponctuels"] },
        { name: "Opérationnel", tag: "Recommandé", accent: "gold", highlight: "Prévention + optimisation M365.", items: ["Tout Essentiel", "Suivi régulier", "Prévention & bonnes pratiques", "Optimisation Microsoft 365"] },
        { name: "Critique", tag: "SLA", accent: "dark", highlight: "SLA & priorisation incidents.", items: ["Tout Opérationnel", "Astreinte (option)", "Priorisation", "Engagements renforcés"] },
      ],
      useCases: [
        { icon: "office", title: "Organisation multi-équipes", text: "Support Microsoft 365 + standardisation des usages, sécurité des postes, accompagnement au changement.", outcome: "Moins de tickets et meilleure productivité." },
        { icon: "industry", title: "Sites industriels", text: "Support IT terrain, continuité de service, procédures et priorisation incidents.", outcome: "Réduction des interruptions." },
        { icon: "store", title: "Réseau retail", text: "Support centralisé, escalades, optimisation postes et accompagnement sécurité.", outcome: "Expérience magasin plus stable." },
      ],
      proof:
        "Un support IT efficace = moins d’interruptions. Nous mettons en place des pratiques claires (diagnostic, escalade, reporting) pour stabiliser votre quotidien.",
      ctas: [
        { label: "Obtenir du support", to: "/contactus" },
        { label: "Parler à un expert", to: "/contactus" },
      ],
    },
    ["it-office.webp", "it-industry.webp", "it-retail.webp"]
  ),

  // ✅ Inventory already has images; we still normalize icon keys + ensure result+image exist
  "gestion-des-inventaires": withUseCaseImages(
    {
      title: "Gestion des inventaires",
      catalogue: {
        title: "Préparer le matériel de votre inventaire",
        text: "Un lecteur connecté au poste et un PDA avec application mobile répondent à des parcours différents. Comparez les équipements et les logiciels, puis faites valider la lecture de vos codes et les échanges avec votre système de gestion.",
        links: [
          { label: "Terminaux PDA pour le comptage", href: "https://store.teledyne.ma/produits/terminaux-mobiles/terminaux-pda/" },
          { label: "Lecteurs de codes-barres", href: "https://store.teledyne.ma/produits/lecteurs/lecteurs-codes-barres/" },
          { label: "Logiciels d’inventaire", href: "https://store.teledyne.ma/produits/logiciels/logiciels-inventaire/" },
        ],
      },
      badge: "Solution TELEDYNE",
      heroImage: "/images/Service/1.webp",
      heroTitle: "Gestion des inventaires",
      heroAccent: "Stock & Immobilisations",
      heroText:
        "Deux types d’inventaires (stock et immobilisations) avec deux applications TELEDYNE : un back-office de pilotage et une application mobile dédiée. Selon votre besoin : déploiement + supervision (stock) ou inventaire A à Z par nos équipes (immos).",
      tags: ["Back-office", "Mobile", "Scan", "Pilotage", "Reporting"],

      stats: [
        { value: "2", label: "Types d’inventaire" },
        { value: "2", label: "Applications" },
        { value: "A→Z", label: "Option immos" },
      ],

      primaryCTA: { label: "Demander une démo", to: "/contactus" },
      secondaryCTA: { label: "Obtenir un devis", to: "/contactus" },

      scopeCards: [
        { icon: "dashboard", title: "Back-office de pilotage", text: "Paramétrage des campagnes, préparation des référentiels, suivi d’avancement, contrôle qualité et reporting. Une vue claire pour piloter l’inventaire en temps réel." },
        { icon: "scan", title: "Application mobile de scan", text: "Collecte terrain rapide via PDA/smartphone : scan, recherche, saisie contrôlée et consolidation. Une application mobile dédiée à chaque type d’inventaire (stock ou immos)." },
        { icon: "data", title: "Rapprochement & consolidation", text: "Rapprochement des données (référentiel vs terrain), détection des écarts, traitement des anomalies et génération d’états exploitables pour décision." },
        { icon: "quality", title: "Supervision & contrôle", text: "Process de contrôle, règles de validation, suivi des écarts et traçabilité des opérations. Objectif : inventaire fiable, auditable et reproductible." },
      ],

      deliverables: [
        { icon: "doc", title: "Dossier de cadrage", text: "Périmètre, règles de gestion, planning, rôles, supports terrain et objectifs de qualité (taux de couverture, écarts, validations)." },
        { icon: "report", title: "Rapports & tableaux de bord", text: "Avancement par site/zone, écarts, anomalies, rapprochements, états de synthèse et exports (selon vos formats)." },
        { icon: "training", title: "Formation & prise en main", text: "Formation back-office et mobile, procédures d’exploitation et bonnes pratiques pour sécuriser l’exécution terrain." },
      ],

      packs: [
        {
          name: "Stock — Supervisé",
          tag: "Déploiement",
          accent: "basic",
          highlight: "Vous réalisez l’inventaire, TELEDYNE vous supervise.",
          items: ["Déploiement des applications (back-office + mobile stock)", "Paramétrage campagne & référentiels", "Supervision / assistance pendant l’inventaire", "Reporting d’avancement & synthèse écarts"],
        },
        {
          name: "Immos — A à Z",
          tag: "Réalisation",
          accent: "gold",
          highlight: "TELEDYNE réalise l’inventaire complet sur site.",
          items: ["Équipe terrain expérimentée (scan & collecte)", "Rapprochement, traitement des écarts, anomalies", "Consolidation & états finaux exploitables", "Restitution & recommandations"],
        },
        {
          name: "Entreprise",
          tag: "SLA",
          accent: "dark",
          highlight: "Pour multi-sites & fortes contraintes.",
          items: ["Pilotage multi-sites, planning et coordination", "Contrôles qualité renforcés (sampling, validations)", "SLA support (selon criticité) + escalade", "Runbook & amélioration continue"],
        },
      ],

      useCases: [
        {
          icon: "warehouse",
          title: "Inventaire de stock — entrepôt / distribution",
          text: "Déploiement des applications, préparation des zones et des référentiels, puis supervision pendant l’exécution : suivi d’avancement, assistance, contrôles et consolidation des résultats.",
          result: "Inventaire structuré, suivi temps réel, écarts maîtrisés.",
          image: "/images/Service/inventaire.webp",
        },
        {
          icon: "building",
          title: "Inventaire des immobilisations — multi-sites",
          text: "Nos équipes se déplacent et réalisent l’inventaire A à Z : scan, collecte, rapprochement, traitement des anomalies et restitution finale.",
          result: "Inventaire immos complet, données prêtes pour audit et décision.",
          image: "/images/Service/inventaire2.webp",
        },
        {
          icon: "globe",
          title: "Inventaires immos — expérience Afrique",
          text: "Missions déjà réalisées avec déplacement et exécution terrain. Capacité à opérer en environnements multi-pays avec méthodologie et coordination.",
          result: "Exécution terrain fiable et industrialisable.",
          image: "/images/Service/inventaire1.webp",
        },
      ],
    },
    // keep existing images; no need to override
    []
  ),
  "developpement-applications": withUseCaseImages(
  {
    key: "developpement-applications",
    heroImage: "/images/Service/2.webp",
    title: "Développement d’applications",
    subtitle: "Web • Mobile • APIs & Intégrations",
    heroTitle: "Applications métiers sur mesure",
    heroText:
      "Nous concevons et développons des applications web et mobiles adaptées à vos processus métiers. De l’UX/UI à la mise en production, nous livrons des solutions performantes, sécurisées et évolutives, intégrées à votre système d’information.",
    tags: [
      "Web",
      "Mobile",
      "API",
      "Intégrations",
      "UX/UI",
      "CI/CD",
      "Sécurité",
    ],

    kpis: [
      { value: "Sur mesure", label: "Fonctionnel métier" },
      { value: "API", label: "Interopérabilité" },
      { value: "Évolutif", label: "Architecture" },
    ],

    scopeCards: [
      {
        icon: "design",
        title: "UX / UI & conception",
        text:
          "Analyse des besoins, parcours utilisateurs, maquettage et validation UX/UI pour des applications efficaces et adoptées.",
      },
      {
        icon: "code",
        title: "Développement applicatif",
        text:
          "Développement web et mobile (front-end, back-end), architectures modernes et bonnes pratiques de sécurité.",
      },
      {
        icon: "api",
        title: "APIs & intégrations",
        text:
          "Connexion avec vos systèmes existants (ERP, WMS, CRM, SI métiers), échanges sécurisés et automatisés.",
      },
      {
        icon: "deploy",
        title: "Déploiement & maintenance",
        text:
          "Mise en production, CI/CD, supervision, maintenance évolutive et accompagnement long terme.",
      },
    ],

    deliverables: [
      {
        icon: "doc",
        title: "Spécifications fonctionnelles",
        text:
          "Cahier des charges clair : périmètre, règles métier, flux, rôles utilisateurs et contraintes techniques.",
      },
      {
        icon: "gear",
        title: "Application livrée",
        text:
          "Application web ou mobile testée, documentée et prête à l’exploitation.",
      },
      {
        icon: "training",
        title: "Documentation & transfert",
        text:
          "Documentation technique et fonctionnelle, accompagnement et formation des équipes.",
      },
    ],

    packs: [
      {
        name: "Essentiel",
        tag: "MVP",
        accent: "basic",
        highlight: "Lancer rapidement une application métier.",
        items: [
          "Cadrage & UX/UI",
          "Développement MVP",
          "Tests & validation",
          "Documentation de base",
        ],
      },
      {
        name: "Opérationnel",
        tag: "Recommandé",
        accent: "gold",
        highlight: "Solution complète et intégrée.",
        items: [
          "Tout Essentiel",
          "APIs & intégrations SI",
          "Sécurité & performances",
          "Maintenance évolutive",
        ],
      },
      {
        name: "Entreprise",
        tag: "SLA",
        accent: "dark",
        highlight: "Pour applications critiques.",
        items: [
          "Tout Opérationnel",
          "CI/CD & supervision",
          "SLA & support renforcé",
          "Amélioration continue",
        ],
      },
    ],

    useCases: [
      {
        icon: "dashboard",
        title: "Application métier interne",
        text:
          "Développement d’applications internes pour le pilotage, la gestion opérationnelle ou le reporting.",
        outcome: "Productivité accrue et processus maîtrisés.",
        image: "/images/Service/App_Interne.webp",
      },
      {
        icon: "mobile",
        title: "Application mobile terrain",
        text:
          "Applications mobiles pour équipes terrain : collecte, saisie, scan, synchronisation temps réel.",
        outcome: "Données fiables et exécution terrain optimisée.",
        image: "/images/Service/App_Terrain.webp",
      },
      {
        icon: "plug",
        title: "Intégration SI & automatisation",
        text:
          "Connexion avec ERP, WMS ou autres outils pour automatiser les flux et réduire les tâches manuelles.",
        outcome: "Interopérabilité et gain de temps.",
        image: "/images/Service/ERP.webp",
      },
    ],

    proof:
      "Notre approche est pragmatique : comprendre vos métiers, livrer rapidement, sécuriser l’architecture et accompagner l’évolution. Chaque projet est pensé pour durer.",

    ctas: [
      { label: "Discuter de votre projet", to: "/contactus" },
      { label: "Demander un devis", to: "/contactus" },
    ],
  },
  // images associées aux use cases (ordre respecté)
  ["dev-dashboard.webp", "dev-mobile.webp", "dev-integration.webp"]
),

};
